// Copyright(C)2025 Sky CO., LTD.
/****************************************************************//*
 @file
    CommonResource.ts

 @brief
    共通 メッセージ定義

 @details

    ・本クラスはメッセージ一覧マクロで出力されたシングルトンの
      メッセージ定義クラスです。本クラスは直接編集せず、
      メッセージ一覧マクロから出力してください。

    ●文言定義（Str_～）
        文言を定義したプロパティです。
        【例】
            let hoge = Resource.xxxResource.getInstance().Str_Hoge;

    ●メッセージボックス定義（Msg_～）
        メッセージボックス用のメッセージを定義したプロパティです。
        【例】
            let info = Resource.xxxResource.getInstance().Msg_Piyo;

 @author
    sky

 @attention

 @note
    2026/06/24 16:14:55
 ****************************************************************///
import { ShowMessageBoxParams } from 'lib-vue-v2/composables/useMessageBox';
import { MessageBoxFlags } from 'lib-vue-v2/components';

export namespace Resource {

    /** 関数でメッセージ定義を取得するための紐づけID */
    export const enum MsgID {
        NotIncludeDataExportError = 'NotIncludeDataExportError',
        CancelMessage = 'CancelMessage',
        UncommittedDataDiscardConfirmation = 'UncommittedDataDiscardConfirmation',
        Import_CompleteInfo = 'Import_CompleteInfo',
        Import_FailedError = 'Import_FailedError',
        Import_RequiredHeaderNotExistWarn = 'Import_RequiredHeaderNotExistWarn',
        Import_DataNotExistWarn = 'Import_DataNotExistWarn',
        Import_RequiredHeaderDuplicateWarn = 'Import_RequiredHeaderDuplicateWarn',
        Preview_ErrorAgentNotInstalled = 'Preview_ErrorAgentNotInstalled',
        ConfirmApplySetting = 'ConfirmApplySetting',
        SetMessageWarnForMessage = 'SetMessageWarnForMessage',
        InputMessageWarnForMessage = 'InputMessageWarnForMessage',
        RegisterMessageWarnForMessage = 'RegisterMessageWarnForMessage',
        RegisterLimitMessageWarnForMessage = 'RegisterLimitMessageWarnForMessage',
        AlreadyRegisteredWarnForLabel = 'AlreadyRegisteredWarnForLabel',
        AlreadyRegisteredInCategoryWarnForLabel = 'AlreadyRegisteredInCategoryWarnForLabel',
        InputRequiredWarnForLabel = 'InputRequiredWarnForLabel',
        SelectMessageWarnForMessage = 'SelectMessageWarnForMessage',
        AlreadyRegisteredWarnForMessage = 'AlreadyRegisteredWarnForMessage',
        SetMessageWarnForLabel = 'SetMessageWarnForLabel',
        AlreadyRegisteredInCategoryWarnForMessage = 'AlreadyRegisteredInCategoryWarnForMessage',
        GetFailedErrorForMessage = 'GetFailedErrorForMessage',
        UpdateFailedErrorForMessage = 'UpdateFailedErrorForMessage',
        Quill_DeleteLink = 'Quill_DeleteLink',
        Quill_NotAllowImgType = 'Quill_NotAllowImgType',
        Quill_NotAllowAnimation = 'Quill_NotAllowAnimation',
        Common_NotAvailableByRestrictions = 'Common_NotAvailableByRestrictions',
        Common_ConfirmApplySetting = 'Common_ConfirmApplySetting',
        DeleteFailedErrorForMessage = 'DeleteFailedErrorForMessage',
        Common_TargetNotFound = 'Common_TargetNotFound',
        Common_CompleteApplySetting = 'Common_CompleteApplySetting',
        Common_FailureApplySetting = 'Common_FailureApplySetting',
        Import_DuplicationData = 'Import_DuplicationData',
        Quill_InsertImageError = 'Quill_InsertImageError',
        Common_ImageMaxSizeError = 'Common_ImageMaxSizeError',
        Common_UnauthorizedSetting = 'Common_UnauthorizedSetting',
        IpAddressRangeError = 'IpAddressRangeError',
        LimitOverDeviceError = 'LimitOverDeviceError',
        EitherRequiredError = 'EitherRequiredError',
        IpAddressFormatError = 'IpAddressFormatError',
        MacAddressFormatError = 'MacAddressFormatError',
        CIDRFormatError = 'CIDRFormatError',
        HostOrIpInvalidInputError = 'HostOrIpInvalidInputError',
        EndIpAddressSameStartError = 'EndIpAddressSameStartError',
        InputRequestMessage = 'InputRequestMessage',
        InputFailedControlCode = 'InputFailedControlCode',
        Common_FailedApplySetting = 'Common_FailedApplySetting',
        Common_ConfirmNotApplySetting = 'Common_ConfirmNotApplySetting',
        Common_Warn_FailedCommunicationMS = 'Common_Warn_FailedCommunicationMS',
        Import_AskTo = 'Import_AskTo',
        SelectLimitOverDeviceError = 'SelectLimitOverDeviceError',
        Common_NotRegistered = 'Common_NotRegistered',
        Common_ConfirmApplySettingWithDetail = 'Common_ConfirmApplySettingWithDetail',
        Common_ConflictApplySetting = 'Common_ConflictApplySetting',
        Common_PleaseSelect = 'Common_PleaseSelect',
        Common_Warning_SettingValidationError = 'Common_Warning_SettingValidationError',
        Export_Failed = 'Export_Failed',
        Export_Complete = 'Export_Complete',
    }

    /** CommonResource */
    export class CommonResource{

        /** メッセージ文言取得関数 */
        public getMsg(id: MsgID): string {
            switch (id) {
                case MsgID.AlreadyRegisteredWarnForLabel: return this.Str_AlreadyRegisteredWarnForLabel;
                case MsgID.AlreadyRegisteredInCategoryWarnForLabel: return this.Str_AlreadyRegisteredInCategoryWarnForLabel;
                case MsgID.InputRequiredWarnForLabel: return this.Str_InputRequiredWarnForLabel;
                case MsgID.SetMessageWarnForLabel: return this.Str_SetMessageWarnForLabel;
                case MsgID.Import_DuplicationData: return this.Str_Import_DuplicationData;
                case MsgID.IpAddressRangeError: return this.Str_IpAddressRangeError;
                case MsgID.EitherRequiredError: return this.Str_EitherRequiredError;
                case MsgID.IpAddressFormatError: return this.Str_IpAddressFormatError;
                case MsgID.MacAddressFormatError: return this.Str_MacAddressFormatError;
                case MsgID.CIDRFormatError: return this.Str_CIDRFormatError;
                case MsgID.HostOrIpInvalidInputError: return this.Str_HostOrIpInvalidInputError;
                case MsgID.EndIpAddressSameStartError: return this.Str_EndIpAddressSameStartError;
                case MsgID.InputRequestMessage: return this.Str_InputRequestMessage;
                case MsgID.InputFailedControlCode: return this.Str_InputFailedControlCode;
                case MsgID.Common_NotRegistered: return this.Str_Common_NotRegistered;
                case MsgID.Common_PleaseSelect: return this.Str_Common_PleaseSelect;
                default: return '';
            }
        }

        /** メッセージ情報取得関数 */
        public getMsgInfo(id: MsgID, replaceMessages?: string[]): ShowMessageBoxParams {
            let showMessageBoxParams = { message: '' }
            switch (id) {
                case MsgID.NotIncludeDataExportError:
                    showMessageBoxParams = this.Msg_NotIncludeDataExportError;
                    break;
                case MsgID.CancelMessage:
                    showMessageBoxParams = this.Msg_CancelMessage;
                    break;
                case MsgID.UncommittedDataDiscardConfirmation:
                    showMessageBoxParams = this.Msg_UncommittedDataDiscardConfirmation;
                    break;
                case MsgID.Import_CompleteInfo:
                    showMessageBoxParams = this.Msg_Import_CompleteInfo;
                    break;
                case MsgID.Import_FailedError:
                    showMessageBoxParams = this.Msg_Import_FailedError;
                    break;
                case MsgID.Import_RequiredHeaderNotExistWarn:
                    showMessageBoxParams = this.Msg_Import_RequiredHeaderNotExistWarn;
                    break;
                case MsgID.Import_DataNotExistWarn:
                    showMessageBoxParams = this.Msg_Import_DataNotExistWarn;
                    break;
                case MsgID.Import_RequiredHeaderDuplicateWarn:
                    showMessageBoxParams = this.Msg_Import_RequiredHeaderDuplicateWarn;
                    break;
                case MsgID.Preview_ErrorAgentNotInstalled:
                    showMessageBoxParams = this.Msg_Preview_ErrorAgentNotInstalled;
                    break;
                case MsgID.ConfirmApplySetting:
                    showMessageBoxParams = this.Msg_ConfirmApplySetting;
                    break;
                case MsgID.SetMessageWarnForMessage:
                    showMessageBoxParams = this.Msg_SetMessageWarnForMessage;
                    break;
                case MsgID.InputMessageWarnForMessage:
                    showMessageBoxParams = this.Msg_InputMessageWarnForMessage;
                    break;
                case MsgID.RegisterMessageWarnForMessage:
                    showMessageBoxParams = this.Msg_RegisterMessageWarnForMessage;
                    break;
                case MsgID.RegisterLimitMessageWarnForMessage:
                    showMessageBoxParams = this.Msg_RegisterLimitMessageWarnForMessage;
                    break;
                case MsgID.SelectMessageWarnForMessage:
                    showMessageBoxParams = this.Msg_SelectMessageWarnForMessage;
                    break;
                case MsgID.AlreadyRegisteredWarnForMessage:
                    showMessageBoxParams = this.Msg_AlreadyRegisteredWarnForMessage;
                    break;
                case MsgID.AlreadyRegisteredInCategoryWarnForMessage:
                    showMessageBoxParams = this.Msg_AlreadyRegisteredInCategoryWarnForMessage;
                    break;
                case MsgID.GetFailedErrorForMessage:
                    showMessageBoxParams = this.Msg_GetFailedErrorForMessage;
                    break;
                case MsgID.UpdateFailedErrorForMessage:
                    showMessageBoxParams = this.Msg_UpdateFailedErrorForMessage;
                    break;
                case MsgID.Quill_DeleteLink:
                    showMessageBoxParams = this.Msg_Quill_DeleteLink;
                    break;
                case MsgID.Quill_NotAllowImgType:
                    showMessageBoxParams = this.Msg_Quill_NotAllowImgType;
                    break;
                case MsgID.Quill_NotAllowAnimation:
                    showMessageBoxParams = this.Msg_Quill_NotAllowAnimation;
                    break;
                case MsgID.Common_NotAvailableByRestrictions:
                    showMessageBoxParams = this.Msg_Common_NotAvailableByRestrictions;
                    break;
                case MsgID.Common_ConfirmApplySetting:
                    showMessageBoxParams = this.Msg_Common_ConfirmApplySetting;
                    break;
                case MsgID.DeleteFailedErrorForMessage:
                    showMessageBoxParams = this.Msg_DeleteFailedErrorForMessage;
                    break;
                case MsgID.Common_TargetNotFound:
                    showMessageBoxParams = this.Msg_Common_TargetNotFound;
                    break;
                case MsgID.Common_CompleteApplySetting:
                    showMessageBoxParams = this.Msg_Common_CompleteApplySetting;
                    break;
                case MsgID.Common_FailureApplySetting:
                    showMessageBoxParams = this.Msg_Common_FailureApplySetting;
                    break;
                case MsgID.Quill_InsertImageError:
                    showMessageBoxParams = this.Msg_Quill_InsertImageError;
                    break;
                case MsgID.Common_ImageMaxSizeError:
                    showMessageBoxParams = this.Msg_Common_ImageMaxSizeError;
                    break;
                case MsgID.Common_UnauthorizedSetting:
                    showMessageBoxParams = this.Msg_Common_UnauthorizedSetting;
                    break;
                case MsgID.LimitOverDeviceError:
                    showMessageBoxParams = this.Msg_LimitOverDeviceError;
                    break;
                case MsgID.Common_FailedApplySetting:
                    showMessageBoxParams = this.Msg_Common_FailedApplySetting;
                    break;
                case MsgID.Common_ConfirmNotApplySetting:
                    showMessageBoxParams = this.Msg_Common_ConfirmNotApplySetting;
                    break;
                case MsgID.Common_Warn_FailedCommunicationMS:
                    showMessageBoxParams = this.Msg_Common_Warn_FailedCommunicationMS;
                    break;
                case MsgID.Import_AskTo:
                    showMessageBoxParams = this.Msg_Import_AskTo;
                    break;
                case MsgID.SelectLimitOverDeviceError:
                    showMessageBoxParams = this.Msg_SelectLimitOverDeviceError;
                    break;
                case MsgID.Common_ConfirmApplySettingWithDetail:
                    showMessageBoxParams = this.Msg_Common_ConfirmApplySettingWithDetail;
                    break;
                case MsgID.Common_ConflictApplySetting:
                    showMessageBoxParams = this.Msg_Common_ConflictApplySetting;
                    break;
                case MsgID.Common_Warning_SettingValidationError:
                    showMessageBoxParams = this.Msg_Common_Warning_SettingValidationError;
                    break;
                case MsgID.Export_Failed:
                    showMessageBoxParams = this.Msg_Export_Failed;
                    break;
                case MsgID.Export_Complete:
                    showMessageBoxParams = this.Msg_Export_Complete;
                    break;
                default:  //何もしない
                    break;
            }

            // メッセージにオプションで指定した文字列を結合
            if (replaceMessages) {
                let messages: string[] =[];
                replaceMessages.forEach((value) => {
                    messages.push(value)
                })
                showMessageBoxParams.message = showMessageBoxParams.message.format(...messages);
            }

            return showMessageBoxParams;
        }

        // #region シングルトンインスタンス管理

        private static _instance: CommonResource;
        /** シングルトンインスタンス取得 */
        public static getInstance(): CommonResource {
            if (!this._instance) {
                this._instance = new CommonResource();
            }
            return this._instance;
        }
        // #endregion // シングルトンインスタンス管理

        // #region メッセージ定義

        get Msg_NotIncludeDataExportError(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '出力データがありません。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_CancelMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '%sを中止しました。',
                caption: '情報',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONINFORMATION |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_UncommittedDataDiscardConfirmation(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '未適用のデータは破棄されます。<br />よろしいですか？',
                caption: '確認',
                flags:
                    MessageBoxFlags.MB_YESNO |
                    MessageBoxFlags.MB_ICONQUESTION |
                    MessageBoxFlags.MB_DEFBUTTON2,
            }
            return msg;
        }

        get Msg_Import_CompleteInfo(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'インポートが完了しました。',
                caption: '情報',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONINFORMATION |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Import_FailedError(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'インポートに失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Import_RequiredHeaderNotExistWarn(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'インポートデータが見つかりません。以下のヘッダが必要です。<br /><br />%s',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Import_DataNotExistWarn(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'インポートデータが見つかりません。ファイルの内容を確認してください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Import_RequiredHeaderDuplicateWarn(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'インポートデータで以下のヘッダ名が重複しているため、インポートできません。<br /><br />%s',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Preview_ErrorAgentNotInstalled(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'プレビューの表示に失敗しました。<br />Ver.21.2以降の「SKYSEA端末機(Windows)」がインストールされた端末でのみ表示できます。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_ConfirmApplySetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '変更した内容を適用しますか？',
                caption: '確認',
                flags:
                    MessageBoxFlags.MB_YESNO |
                    MessageBoxFlags.MB_ICONQUESTION |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_SetMessageWarnForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '%sを設定してください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_InputMessageWarnForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '%sを入力してください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_RegisterMessageWarnForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '%sを登録してください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_RegisterLimitMessageWarnForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'これ以上登録することはできません。<br />%sの登録上限は%s件です。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Str_AlreadyRegisteredWarnForLabel(): string {
            return 'すでに同じ%sが登録されています。';
        }

        get Str_AlreadyRegisteredInCategoryWarnForLabel(): string {
            return 'すでに同じ%sが%sに登録されています。';
        }

        get Str_InputRequiredWarnForLabel(): string {
            return '入力必須項目です。';
        }

        get Msg_SelectMessageWarnForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '%sを選択してください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_AlreadyRegisteredWarnForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'すでに同じ%sが登録されています。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Str_SetMessageWarnForLabel(): string {
            return '%sを設定してください。';
        }

        get Msg_AlreadyRegisteredInCategoryWarnForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'すでに同じ%sが%sに登録されています。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_GetFailedErrorForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'データの取得に失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_UpdateFailedErrorForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'データの更新に失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Quill_DeleteLink(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '挿入しているリンクを削除します。<br />よろしいですか？',
                caption: '確認',
                flags:
                    MessageBoxFlags.MB_YESNO |
                    MessageBoxFlags.MB_ICONQUESTION |
                    MessageBoxFlags.MB_DEFBUTTON2,
            }
            return msg;
        }

        get Msg_Quill_NotAllowImgType(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '埋め込み可能な画像ファイルは以下の拡張子のファイルです。<br />JPEG、JPG、PNG、TIF、TIFF、BMP、GIF',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Quill_NotAllowAnimation(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'アニメーションGIFは埋め込みできません。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_NotAvailableByRestrictions(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'この機能を利用できなくなりました。<br />ログアウトします。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_ConfirmApplySetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '設定を適用します。<br />よろしいですか？',
                caption: '確認',
                flags:
                    MessageBoxFlags.MB_YESNO |
                    MessageBoxFlags.MB_ICONQUESTION |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_DeleteFailedErrorForMessage(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'データの削除に失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_TargetNotFound(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'すでに"%s"は削除されています。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_CompleteApplySetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '設定の適用が完了しました。',
                caption: '情報',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONINFORMATION |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_FailureApplySetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '適用に失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Str_Import_DuplicationData(): string {
            return '%sが不正です。[%s]行目に同一の%sが存在します。';
        }

        get Msg_Quill_InsertImageError(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '画像ファイルの埋め込みに失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_ImageMaxSizeError(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'ファイルサイズが大きすぎます。<br />%s以下のファイルを使用してください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_UnauthorizedSetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '本ユーザーでは設定できません。<br />システム管理者にお問い合わせください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Str_IpAddressRangeError(): string {
            return '開始アドレスに終了アドレスよりも大きい値が指定されています。';
        }

        get Msg_LimitOverDeviceError(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'これ以上登録することはできません。<br />%sの登録上限は%s台です。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Str_EitherRequiredError(): string {
            return '%s／%sのどちらかは入力してください。';
        }

        get Str_IpAddressFormatError(): string {
            return '0.0.0.0～255.255.255.255の範囲で入力してください。';
        }

        get Str_MacAddressFormatError(): string {
            return '00-00-00-00-00-00～FF-FF-FF-FF-FF-FFの範囲で入力してください。';
        }

        get Str_CIDRFormatError(): string {
            return '192.168.1.0/24の形式で入力してください。';
        }

        get Str_HostOrIpInvalidInputError(): string {
            return '以下の文字および半角英数字以外は使用できません。<br />.（半角ピリオド）、-（半角ハイフン）、_（半角アンダーバー）';
        }

        get Str_EndIpAddressSameStartError(): string {
            return '終了アドレスに開始アドレスと同じ値が指定されています。';
        }

        get Str_InputRequestMessage(): string {
            return '%sを入力してください。';
        }

        get Str_InputFailedControlCode(): string {
            return '制御コードは使用できません。';
        }

        get Msg_Common_FailedApplySetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '設定の適用に失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Common_ConfirmNotApplySetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '適用せずに終了してよろしいですか？',
                caption: '確認',
                flags:
                    MessageBoxFlags.MB_YESNO |
                    MessageBoxFlags.MB_ICONQUESTION |
                    MessageBoxFlags.MB_DEFBUTTON2,
            }
            return msg;
        }

        get Msg_Common_Warn_FailedCommunicationMS(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'マスターサーバーとの通信に失敗しました。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Import_AskTo(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '設定ファイルのインポートを行います。<br />インポートファイルの内容で設定の追加／編集を行います。<br />よろしいですか？',
                caption: '確認',
                flags:
                    MessageBoxFlags.MB_YESNO |
                    MessageBoxFlags.MB_ICONQUESTION |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_SelectLimitOverDeviceError(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'これ以上選択できません。<br />%sの登録上限は%s台です。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Str_Common_NotRegistered(): string {
            return '%sが登録されていません。';
        }

        get Msg_Common_ConfirmApplySettingWithDetail(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '変更した内容を適用しますか？<br />%s',
                caption: '確認',
                flags:
                    MessageBoxFlags.MB_YESNO |
                    MessageBoxFlags.MB_ICONQUESTION |
                    MessageBoxFlags.MB_NO_DEFBUTTON,
            }
            return msg;
        }

        get Msg_Common_ConflictApplySetting(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '別ユーザーによりデータが更新されたため、適用に失敗しました。<br />最新の情報を取得します。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Str_Common_PleaseSelect(): string {
            return '%sを選択してください。';
        }

        get Msg_Common_Warning_SettingValidationError(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: '設定した内容に不正な項目が存在するため、適用できません。<br />設定内容を確認してください。',
                caption: '警告',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONWARNING |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Export_Failed(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'エクスポートに失敗しました。',
                caption: 'エラー',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONERROR |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        get Msg_Export_Complete(): ShowMessageBoxParams {
            const msg: ShowMessageBoxParams = {
                message: 'CSVファイルの出力を完了しました。',
                caption: '情報',
                flags:
                    MessageBoxFlags.MB_OK |
                    MessageBoxFlags.MB_ICONINFORMATION |
                    MessageBoxFlags.MB_DEFBUTTON1,
            }
            return msg;
        }

        // #endregion // メッセージ定義

    }
}
// Copyright(C)2025 Sky CO., LTD.
